import { AccountingDashboardFinancialDailyRow } from '@/renderer/types/accounting';
import React from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  buildCashflowChartData,
  formatCompactCurrency,
  formatCurrencyValue,
} from './accountingDashboard';

interface AccountingCashflowChartCardProps {
  rows: AccountingDashboardFinancialDailyRow[];
}

const SERIES_LABEL: Record<string, string> = {
  income: 'Income',
  expense: 'Expense',
  pos: 'POS Sales',
  accommodation: 'Accommodation',
};

export function AccountingCashflowChartCard({
  rows,
}: AccountingCashflowChartCardProps) {
  const data = buildCashflowChartData(rows);
  const totalIncome = rows.reduce((sum, row) => sum + row.income_amount, 0);
  const totalExpense = rows.reduce(
    (sum, row) => sum + row.expense_amount + row.cogs_amount,
    0,
  );
  const totalPos = rows.reduce((sum, row) => sum + row.pos_revenue_amount, 0);
  const totalAccommodation = rows.reduce(
    (sum, row) => sum + row.accommodation_revenue_amount,
    0,
  );

  return (
    <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-950">
            Cashflow overview
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            Income and outgoing activity across the selected period.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
            Income {formatCurrencyValue(totalIncome)}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-orange-400" />
            Expense {formatCurrencyValue(totalExpense)}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-blue-500" />
            POS {formatCurrencyValue(totalPos)}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-purple-500" />
            Accommodation {formatCurrencyValue(totalAccommodation)}
          </span>
        </div>
      </div>

      <div className="mt-5 h-[320px]">
        {!data.length ? (
          <div className="flex h-full items-center justify-center rounded-[24px] border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500">
            No financial activity has been posted for this period yet.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={data}
              margin={{ top: 12, right: 12, left: -20, bottom: 0 }}
            >
              <defs>
                <linearGradient
                  id="accountingIncomeFill"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor="#16a34a" stopOpacity={0.22} />
                  <stop offset="95%" stopColor="#16a34a" stopOpacity={0} />
                </linearGradient>
                <linearGradient
                  id="accountingExpenseFill"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor="#f97316" stopOpacity={0.18} />
                  <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                </linearGradient>
                <linearGradient
                  id="accountingPosFill"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor="#2563eb" stopOpacity={0.14} />
                  <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                </linearGradient>
                <linearGradient
                  id="accountingAccommodationFill"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor="#a855f7" stopOpacity={0.14} />
                  <stop offset="95%" stopColor="#a855f7" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="#e2e8f0" />
              <XAxis
                dataKey="label"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#94a3b8', fontSize: 11 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#94a3b8', fontSize: 11 }}
                tickFormatter={(value) => formatCompactCurrency(Number(value))}
              />
              <Tooltip
                formatter={(value, name) => [
                  formatCurrencyValue(Number(value ?? 0)),
                  SERIES_LABEL[String(name)] ?? String(name),
                ]}
                contentStyle={{
                  borderRadius: 16,
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 14px 30px rgba(15, 23, 42, 0.08)',
                }}
              />
              <Area
                type="monotone"
                dataKey="income"
                stroke="#16a34a"
                strokeWidth={3}
                fill="url(#accountingIncomeFill)"
                name="income"
              />
              <Area
                type="monotone"
                dataKey="expense"
                stroke="#f97316"
                strokeWidth={3}
                fill="url(#accountingExpenseFill)"
                name="expense"
              />
              <Area
                type="monotone"
                dataKey="pos"
                stroke="#2563eb"
                strokeWidth={2}
                fill="url(#accountingPosFill)"
                name="pos"
              />
              <Area
                type="monotone"
                dataKey="accommodation"
                stroke="#a855f7"
                strokeWidth={2}
                fill="url(#accountingAccommodationFill)"
                name="accommodation"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
